import { useSupportSheet } from "../components/SupportSheet";
import {
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BodyCopy } from '../components/BodyCopy';
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  SupportIcon,
} from 'lola-framework-ui-test';
import { AmountMismatchTopDrawer } from '../components/AmountMismatchTopDrawer';
import { BarcodeDrawerSlot } from '../components/BarcodeDrawerSlot';
import { Popup } from '../components/Popup';
import { HowToDepositHelpIcon } from '../assets/icons';
import { ScrollFadeIndicator } from '../components/ScrollFadeIndicator';
import { useGalleryStore } from '../gallery/galleryStore';
import { useStore } from '../store/globalState';
import {
  accentInkOnSurface,
  type BrandMeshColors,
} from '../utils/accentInkOnSurface';
import { useCashDepositStore } from '../store/useCashDepositStore';
import { useCashStoresReelCenter } from '../hooks/useCashStoresReelCenter';
import { APP_ROUTES, CASH_DEPOSIT_ROUTES } from '../constants';
import { formatHeroAmount } from '../utils';
import { unlockAmountMismatchAudio } from '../utils/playAmountMismatchAlert';
import {
  CASH_DEPOSIT_AMOUNT,
  CASH_DEPOSIT_BARCODE_EXP_MINUTES,
  CASH_DEPOSIT_BARCODE_VALUE,
  CASH_DEPOSIT_CURRENCY,
  cashDepositMockMerchants,
  type CashDepositMockMerchant,
} from '../gallery/cashDepositMock';
import { Navbar } from '../components/Navbar';

/** How many times the merchant directory is repeated to build the spinning reel.
 *  Three copies is enough to spin across one full directory and still show
 *  neighbors on both sides of the settled slot. */
const REEL_LOOPS = 3;

const EXPIRED_BARCODE_AT = new Date(0).toISOString();

const MerchantLogo = ({
  merchant,
  name,
}: {
  merchant: CashDepositMockMerchant;
  name: string;
}) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span className="cash-stores-logo-fallback" aria-hidden>
        {name}
      </span>
    );
  }

  return (
    <img
      src={merchant.logo}
      alt={name}
      className="cash-stores-logo"
      onError={() => setFailed(true)}
    />
  );
};

export type CashStoresGalleryPreview = 'expired' | 'amount-mismatch';

export interface CashStoresPageProps {
  /** Gallery-only: mount with a preset barcode drawer state. */
  preview?: CashStoresGalleryPreview;
}

/**
 * Gallery-safe cash-stores summary. Always shows the selected-merchant reel
 * (widget gates it on requireCustomerToChooseChain). How-to row + barcode
 * drawer match the cash-deposit widget; merchants and barcode are mocked.
 */
export const CashStoresPage = memo(({ preview }: CashStoresPageProps = {}) => {
  const expiredPreview = preview === 'expired';
  const mismatchPreview = preview === 'amount-mismatch';
  const galleryDrawerOpen = expiredPreview || mismatchPreview;
  const { t } = useTranslation();
  const { theme } = useStore();
  const selectedMerchantId = useCashDepositStore((s) => s.selectedMerchantId);
  const setSelectedMerchantId = useCashDepositStore(
    (s) => s.setSelectedMerchantId,
  );
  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    bodyCopy,
    footerColor,
    title,
    dropzoneColors,
  } = colorConfig;
  const partnerColors = useGalleryStore(
    (s) => s.rawTheme.colors as BrandMeshColors,
  );
  const howToTitle = accentInkOnSurface(partnerColors, background, bodyCopy);
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("cash-deposit");
  const [showBarcodeDrawer, setShowBarcodeDrawer] = useState(galleryDrawerOpen);
  const [showAmountMismatch, setShowAmountMismatch] = useState(mismatchPreview);
  const [barcodeExpiresAt, setBarcodeExpiresAt] = useState<string | undefined>(
    () => {
      if (expiredPreview) return EXPIRED_BARCODE_AT;
      if (mismatchPreview) {
        return new Date(
          Date.now() + CASH_DEPOSIT_BARCODE_EXP_MINUTES * 60_000,
        ).toISOString();
      }
      return undefined;
    },
  );

  const merchants = cashDepositMockMerchants;
  const resolvedMerchantId =
    selectedMerchantId && merchants.some((m) => m.id === selectedMerchantId)
      ? selectedMerchantId
      : merchants[0]?.id ?? null;

  const heroAmount = formatHeroAmount(CASH_DEPOSIT_AMOUNT, CASH_DEPOSIT_CURRENCY);

  const reel = useMemo(() => {
    if (merchants.length === 0) return [];
    const arr: CashDepositMockMerchant[] = [];
    for (let i = 0; i < REEL_LOOPS; i++) arr.push(...merchants);
    return arr;
  }, [merchants]);

  const selectedIndex = merchants.findIndex((m) => m.id === resolvedMerchantId);
  const targetIndex =
    selectedIndex < 0
      ? -1
      : (REEL_LOOPS - 2) * merchants.length + selectedIndex;

  const {
    viewportRef,
    trackRef,
    targetRef,
    translate,
    reelPhase,
    handleReelEnd,
  } = useCashStoresReelCenter(targetIndex, reel.length);

  useEffect(() => {
    if (!mismatchPreview) return;
    unlockAmountMismatchAudio();
    if (!selectedMerchantId && resolvedMerchantId) {
      setSelectedMerchantId(resolvedMerchantId);
    }
  }, [
    mismatchPreview,
    resolvedMerchantId,
    selectedMerchantId,
    setSelectedMerchantId,
  ]);

  const handleCreateBarcode = () => {
    if (!resolvedMerchantId) return;
    if (!selectedMerchantId) setSelectedMerchantId(resolvedMerchantId);
    setBarcodeExpiresAt(
      new Date(
        Date.now() + CASH_DEPOSIT_BARCODE_EXP_MINUTES * 60_000,
      ).toISOString(),
    );
    unlockAmountMismatchAudio();
    setShowBarcodeDrawer(true);
  };

  const closeBarcodeDrawer = () => {
    setShowBarcodeDrawer(false);
    setShowAmountMismatch(false);
  };

  /** Gallery-only cheat: no SSE. Double-click the cash amount to preview the top drawer. */
  const handlePreviewAmountMismatch = () => {
    handleCreateBarcode();
    setShowAmountMismatch(true);
  };

  const themeVars = {
    '--cash-stores-accent': backgroundBtn,
    '--cash-stores-amount-border': bodyCopy,
    '--cash-stores-card-border': bodyCopy,
    '--cash-stores-card-surface': background,
    '--how-to-title': howToTitle,
  } as React.CSSProperties;

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="cash-stores-page need-help-header"
      >
        <Layout.Header style={themeVars}>
          <Navbar
            title={t('transactions.needHelp')}
            align="right"
            color={bodyCopy}
            onBackClick={() => navigate(APP_ROUTES.CASH_DEPOSIT_LANDING_PAGE)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>

        <Layout.Content className="cash-stores-section" style={themeVars}>
          <ScrollFadeIndicator color={bodyCopy} />
          <section className="cash-stores-copy">
            <GradientText
              as="h1"
              textColor={bodyCopy}
              className="cash-stores-title"
            >
              {t('cashStores.title')}
            </GradientText>
            <BodyCopy textColor={bodyCopy} className="cash-stores-subtitle">
              {t('cashStores.subtitle')}
            </BodyCopy>
          </section>

          {reel.length > 0 ? (
            <section
              className="cash-stores-reel"
              data-phase={reelPhase}
              aria-label={t('stores.merchantsLabel')}
            >
              <div className="cash-stores-reel-viewport" ref={viewportRef}>
                <div
                  className="cash-stores-reel-track"
                  ref={trackRef}
                  style={{ transform: `translateX(${translate}px)` }}
                  onTransitionEnd={handleReelEnd}
                >
                  {reel.map((merchant, index) => {
                    const isTarget = index === targetIndex;
                    const name = t(merchant.nameKey);
                    return (
                      <div
                        key={`${merchant.id}-${index}`}
                        ref={isTarget ? targetRef : undefined}
                        className={`cash-stores-reel-item${
                          isTarget && reelPhase === 'landed' ? ' is-selected' : ''
                        }`}
                        aria-hidden={!isTarget}
                      >
                        <MerchantLogo merchant={merchant} name={name} />
                      </div>
                    );
                  })}
                </div>
                <span className="cash-stores-reel-fade cash-stores-reel-fade--left" />
                <span className="cash-stores-reel-fade cash-stores-reel-fade--right" />
              </div>
            </section>
          ) : null}

          <section
            className="cash-stores-amount-card"
            aria-label={t('cashStores.amountLabel')}
            onDoubleClick={handlePreviewAmountMismatch}
          >
            <BodyCopy textColor={bodyCopy} className="cash-stores-amount-label">
              {t('cashStores.amountLabel')}
            </BodyCopy>
            <GradientText
              as="p"
              textColor={bodyCopy}
              className="cash-stores-amount-value"
            >
              {heroAmount.prefix}
              {heroAmount.intPart}.<label>{heroAmount.decPart}</label>
              <span className="cash-stores-amount-currency">
                {heroAmount.suffix}
              </span>
            </GradientText>
          </section>

          <button
            type="button"
            className="cash-stores-how-to"
            onClick={() =>
              navigate(
                CASH_DEPOSIT_ROUTES.HOW_TO_PAY_IN_CASH,
              )
            }
          >
            <span className="cash-stores-how-to-mark">
              <span className="cash-stores-how-to-mark-shadow" aria-hidden />
              <span
                className="cash-stores-how-to-mark-disc"
                style={{ background }}
              >
                <HowToDepositHelpIcon size={18} colors={dropzoneColors} />
              </span>
            </span>
            <GradientText
              as="span"
              textColor={bodyCopy}
              className="cash-stores-how-to-label"
            >
              {t('cashStores.howToDeposit')}
            </GradientText>
          </button>
        </Layout.Content>

        <Layout.Footer style={themeVars}>
          <section className="valid-cards">
            <section className="valid-cards-container cash-stores-disclaimer-block">
              <BodyCopy
                className="cash-stores-disclaimer-line"
                textColor={footerColor}
              >
                {t('cashStores.disclaimerLine1')}{' '}
                {t('cashStores.disclaimerLine2')}{' '}
                <GradientText
                  as="span"
                  className="cash-stores-terms-link"
                  textColor={title}
                  onClick={() => navigate(CASH_DEPOSIT_ROUTES.LEGAL)}
                >
                  {t('cashStores.termsLink')}
                </GradientText>
                .
              </BodyCopy>
            </section>
            <section className="footer-section">
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={!resolvedMerchantId}
                onClick={handleCreateBarcode}
              >
                {t('cashStores.cta')}
              </Button>
            </section>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup
        visible={showBarcodeDrawer}
        onClose={closeBarcodeDrawer}
      >
        <>
          {showAmountMismatch ? (
            <AmountMismatchTopDrawer
              expectedAmount={CASH_DEPOSIT_AMOUNT}
              currency={CASH_DEPOSIT_CURRENCY}
              onDismiss={() => setShowAmountMismatch(false)}
            />
          ) : null}
          <BarcodeDrawerSlot
            amount={CASH_DEPOSIT_AMOUNT}
            currency={CASH_DEPOSIT_CURRENCY}
            barcodeValue={CASH_DEPOSIT_BARCODE_VALUE}
            expMinutes={CASH_DEPOSIT_BARCODE_EXP_MINUTES}
            expiresAt={barcodeExpiresAt}
            onClose={closeBarcodeDrawer}
            onAmountDoubleClick={() => setShowAmountMismatch(true)}
          />
        </>
      </Popup>
      {supportSheet}
    </>
  );
});
